import React from 'react';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { colors } from '@skyslope/mache';
import { useHistory, useParams } from 'react-router-dom';
import { Documents } from '../common/documents';
import type { IDoc } from '../common/documents/types';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../common/launchDarkly';
import NavHeader from '../common/navigation/NavHeader';
import { RecipientRole } from '../common/recipients/types';
import Footer from '../components/common/Footer';
import { Shelf } from '../components/templateCreate/Shelf';
import { HeaderContext } from '../context/header-context';
import { getUsersGroups } from '../lib/api/account/accountApi';
import { progressPercentageForDocuments } from '../lib/api/common/progressPercentage';
import { SHELF_WIDTH, chameleonElId } from '../lib/constants';
import { preferencesUrl } from '../lib/environment-config';
import { createRandomId } from '../lib/randomId';
import { useDispatch, useShallowSelector } from '../lib/reduxHooks';
import { errorToast } from '../store/pageFrame/actions';
import type { IDocument } from '../store/senderBuild/types';
import * as actions from '../store/templateCreate/actions';
import type { ITemplateRecipient, ITemplate } from '../store/templateCreate/template-types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  documentsWrapper: {
    position: 'fixed',
    left: SHELF_WIDTH,
    right: 0,
    bottom: 0,
    top: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'auto',
  },
  paper: {
    height: '75%',
    width: '75%',
    display: 'flex',
    boxShadow: 'none',
    border: `1px solid ${colors.grey[500]}`,
    flexDirection: 'column',
    alignItems: 'stretch',
    '@media(max-width: 1024px)': {
      width: '85%',
    },
    '@media(max-width: 768px)': {
      width: '95%',
    },
  },
  centerHeader: {
    justifyContent: 'center',
  },
  documents: {
    padding: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
    width: 'calc(100% - 64px)',
    overflow: 'auto',
  },
  legalLinksSection: {
    borderTop: `1px solid ${colors.grey[400]}`,
    height: '72px',
    position: 'fixed',
    bottom: '0px',
    padding: '15px',
    width: '360px',
    left: '0px',
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  legalLinks: {
    display: 'inline-block',
    flex: 'auto',
  },
  anchor: {
    color: colors.grey[800],
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
    fontSize: 13,
  },
  spacer: {
    padding: '0 12px',
    fontSize: 13,
    display: 'inline-block',
  },
}));

function TemplateCreate({ flags }: { flags: LaunchDarklyFlags }) {
  const store = useShallowSelector((state) => ({
    templateCreate: state.templateCreate,
    recipientTitles: state.pageFrame.settings?.account.template?.recipientTitles,
    templateId: state.templateCreate.template?.id,
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
  }));

  const dispatch = useDispatch();
  const classes = useStyles();
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const headerContext = React.useContext(HeaderContext);
  const hideNavItems = flags?.['hide-build-ui-elements'] ?? false;
  const showPreferencesMenuItem = flags?.['add-automatic-time-stamp-signatures-to-user-preferences'];
  const recipients = store.templateCreate.template?.recipients;
  const disabled = recipients?.length === 1;
  const recipientExistsWithNoTitle = recipients?.some((r) => r.recipientTitle === '');

  React.useEffect(() => {
    dispatch(actions.fetchTemplate(params.id));
    headerContext.setContent(
      <NavHeader
        labelName="Create template"
        breadcrumb={{ name: 'Templates', onClick: () => history.push('/templates') }}
        hideNavItems={hideNavItems}
        preferencesUrl={preferencesUrl}
        showPreferences={showPreferencesMenuItem}
        getUsersGroups={getUsersGroups}
        flags={flags}
      />
    );
    return () => {
      headerContext.setContent(null);
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.settingsFetched]);

  if (!store.templateCreate.template) {
    return null;
  }

  function updateTemplate(patchBody: Partial<ITemplate>) {
    dispatch(actions.updateTemplate(patchBody));
  }

  function addRecipient() {
    if (recipients && recipients.length >= 25) {
      dispatch(errorToast("You can't add more than 25 recipients."));
      return;
    }

    const tempId = createRandomId();
    dispatch(
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      actions.addRecipient({
        id: tempId,
        role: RecipientRole.Signer,
        recipientTitle: '',
        isNotCreated: true,
      })
    );
  }

  function updateRecipient(recipientId: string, update: Partial<ITemplateRecipient>) {
    dispatch(actions.updateRecipient(recipientId, update));
  }

  function changeRecipientTitle(recipientId: string, newTitle: string) {
    dispatch(actions.changeRecipientTitle(recipientId, newTitle));
  }

  function handleUploadDocument(tempId: string, file: File) {
    dispatch(actions.uploadDocument(tempId, file));
  }

  function handleReorderDocuments(documents: IDocument[]) {
    dispatch(actions.reorderDocuments(documents));
  }

  function handleDeleteDocument(documentId: string) {
    dispatch(actions.deleteDocument(documentId));
  }

  function handleUpdateDocuments(documents: IDocument[]) {
    dispatch(actions.updateDocuments(documents));
  }

  function handleError(errStr: string) {
    dispatch(errorToast(errStr));
  }

  function handleDeleteRecipient(templateId: string, recipient: ITemplateRecipient) {
    dispatch(actions.deleteRecipient(templateId, recipient));
  }

  return (
    <div className={classes.root}>
      <Shelf
        template={store.templateCreate.template!}
        updateTemplate={updateTemplate}
        addRecipient={addRecipient}
        updateRecipient={updateRecipient}
        changeRecipientTitle={changeRecipientTitle}
        recipientTitles={store.recipientTitles}
        deleteDisabled={disabled}
        templateId={store.templateId}
        deleteRecipient={handleDeleteRecipient}
        hideAction={hideNavItems}
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        userOrigin={store.settings?.account.userOrigin}
      />
      <div className={classes.legalLinksSection}>
        <div className={classes.legalLinks} data-spec="legalLinks">
          <a className={classes.anchor} href="https://support.skyslope.com/hc/en-us" target="blank">
            Support
          </a>
          <span className={classes.spacer}>|</span>
          <a className={classes.anchor} href="https://skyslope.com/terms-conditions/" target="blank">
            Terms
          </a>
          <span className={classes.spacer}>|</span>
          <a className={classes.anchor} href="https://skyslope.com/privacy-policy/" target="blank">
            Privacy
          </a>
        </div>
      </div>
      <div className={classes.documentsWrapper}>
        <Paper className={classes.paper} id={chameleonElId(19)}>
          <Documents
            documents={store.templateCreate.template?.documents as IDoc[]}
            handleUploadDocument={handleUploadDocument}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            handleReorderDocuments={handleReorderDocuments}
            handleDeleteDocument={handleDeleteDocument}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            handleUpdateDocuments={handleUpdateDocuments}
            handleError={handleError}
            progressPercentageForDocuments={progressPercentageForDocuments}
            rootClassName={store.templateCreate.template?.documents.length === 0 ? classes.centerHeader : ''}
            documentsClassName={classes.documents}
          />
        </Paper>
      </div>

      {/* This is intended to sit on top of the header bar, and the .MuiAppBar-root class has z-index: 1100 */}
      <div className="fixed top-16 right-[42px] z-[1100] flex items-center">
        {(store.templateCreate.hasSavedAtLeastOnce || store.templateCreate.showAutoSaveIndicator) && (
          <Typography className="text-grey-600" variant="body1">
            {store.templateCreate.showAutoSaveIndicator ? 'Saving Changes...' : 'All Changes Saved!'}
          </Typography>
        )}
      </div>

      <Footer customLeft="none" customWidth="calc(100VW - 359px)">
        <Button color="primary" onClick={() => history.push('/templates')}>
          Exit
        </Button>
        <Button
          data-spec="next-button"
          variant="contained"
          color="primary"
          disabled={
            !store.templateCreate.template?.documents.length ||
            !store.templateCreate.template?.recipients.filter((r) => !r.isNotCreated).length ||
            store.templateCreate.template.documents.some((d) => d.isUploading) ||
            recipientExistsWithNoTitle
          }
          onClick={() => history.push(`/templates/${store.templateCreate.template?.id}`)}
        >
          Next
        </Button>
      </Footer>
    </div>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(TemplateCreate);
